import { useEffect, useState } from 'react';
import {
  ShoppingCart,
  User,
  DollarSign,
  Building,
  Hash,
  CheckCircle,
  XCircle,
  MinusCircle,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { formatDateTime, formatCurrency } from '@/lib/utils';
import { ordersApi } from '../api';
import type { Order, ComputedAttribution, RuleTraceItem } from '../types';

interface OrderDetailModalProps {
  order: Order | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onViewJourney?: (orderId: number) => void;
}

function InfoRow({
  icon: Icon,
  label,
  value,
  mono = false,
  valueClassName = '',
}: {
  icon: React.ElementType;
  label: string;
  value: string | React.ReactNode;
  mono?: boolean;
  valueClassName?: string;
}) {
  return (
    <div className="flex items-start gap-3 py-2.5">
      <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center">
        <Icon className="h-4 w-4 text-slate-500" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-slate-500 mb-0.5">{label}</p>
        <p className={`text-sm text-slate-800 ${mono ? 'font-mono' : 'font-medium'} ${valueClassName} truncate`}>
          {value}
        </p>
      </div>
    </div>
  );
}

export function OrderDetailModal({
  order,
  open,
  onOpenChange,
}: OrderDetailModalProps) {
  const [attribution, setAttribution] = useState<ComputedAttribution | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && order) {
      setLoading(true);
      setError(null);
      ordersApi
        .getComputedAttribution(order.shopifyOrderId)
        .then((data) => {
          setAttribution(data);
        })
        .catch((err) => {
          setError(err instanceof Error ? err.message : 'Failed to load attribution details');
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setAttribution(null);
    }
  }, [open, order]);

  if (!order) return null;

  const orderDate = order.shopifyCreatedAt || order.createdAt;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader className="pb-4 border-b flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-slate-100 flex items-center justify-center">
              <ShoppingCart className="h-5 w-5 text-slate-600" />
            </div>
            <div className="flex-1">
              <DialogTitle className="text-base">
                Order #{order.shopifyOrderNumber}
              </DialogTitle>
              <DialogDescription className="text-xs">
                {formatDateTime(orderDate)}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="py-4 space-y-6 overflow-y-auto flex-1">
          {/* Order Information */}
          <div>
            <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
              Order Information
            </h4>
            <div className="bg-slate-50 rounded-lg p-3 divide-y divide-slate-200/60">
              <InfoRow
                icon={User}
                label="Customer"
                value={order.email || 'Unknown'}
              />
              <InfoRow
                icon={DollarSign}
                label="Order Value"
                value={formatCurrency(order.totalPrice, order.currency)}
                valueClassName="text-base font-semibold"
              />
              {order.customerId && (
                <InfoRow
                  icon={Hash}
                  label="Customer ID"
                  value={order.customerId}
                  mono
                />
              )}
            </div>
          </div>

          {/* Attribution Result */}
          <div>
            <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
              Attribution Result
            </h4>
            {loading ? (
              <div className="bg-slate-50 rounded-lg p-3 space-y-3">
                <Skeleton className="h-8 w-32" />
                <Skeleton className="h-5 w-24" />
              </div>
            ) : error ? (
              <div className="bg-red-50 rounded-lg p-3 border border-red-200">
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            ) : (
              <div className="bg-emerald-50 rounded-lg p-3 border border-emerald-200 divide-y divide-emerald-200/60">
                <InfoRow
                  icon={Building}
                  label="Attributed To"
                  value={attribution?.distributor?.name || order.attributedDistributorId || 'Unattributed'}
                  valueClassName="text-emerald-700"
                />
                <InfoRow
                  icon={Hash}
                  label="Distributor Code"
                  value={attribution?.distributor?.code || attribution?.distributor?.id || order.attributedDistributorId || '—'}
                  mono
                />
                {(attribution?.ruleName || order.rule?.name) && (
                  <InfoRow
                    icon={CheckCircle}
                    label="Matched Rule"
                    value={attribution?.ruleName || order.rule?.name || '—'}
                  />
                )}
              </div>
            )}
          </div>

          {/* Rule Evaluation Trace */}
          <div>
            <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">
              Rule Evaluation Trace
            </h4>
            {loading ? (
              <div className="space-y-2">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="rounded-lg bg-slate-50 p-3">
                    <div className="flex items-start gap-3">
                      <Skeleton className="h-6 w-6 rounded-full" />
                      <div className="flex-1 space-y-2">
                        <div className="flex items-center justify-between">
                          <Skeleton className="h-4 w-32" />
                          <Skeleton className="h-5 w-16 rounded-full" />
                        </div>
                        <Skeleton className="h-3 w-48" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : attribution?.ruleTrace && attribution.ruleTrace.length > 0 ? (
              <div className="space-y-2">
                {attribution.ruleTrace.map((trace, index) => (
                  <RuleTraceCard key={trace.ruleId} trace={trace} index={index} />
                ))}
              </div>
            ) : (
              <div className="bg-slate-50 rounded-lg p-4 text-center">
                <p className="text-sm text-slate-500">No rule trace available</p>
              </div>
            )}
          </div>

        </div>
      </DialogContent>
    </Dialog>
  );
}

function RuleTraceCard({ trace, index }: { trace: RuleTraceItem; index: number }) {
  const isMatched = trace.matched;
  const wasEvaluated = trace.evaluated;

  const getStatusConfig = () => {
    if (isMatched) {
      return {
        icon: CheckCircle,
        iconColor: 'text-emerald-600',
        badge: 'success' as const,
        text: 'Matched',
        bg: 'bg-emerald-50 border-emerald-200',
      };
    }
    if (!wasEvaluated) {
      return {
        icon: MinusCircle,
        iconColor: 'text-slate-400',
        badge: 'secondary' as const,
        text: 'Skipped',
        bg: 'bg-slate-50 border-slate-200',
      };
    }
    return {
      icon: XCircle,
      iconColor: 'text-slate-500',
      badge: 'secondary' as const,
      text: 'No Match',
      bg: 'bg-slate-50 border-slate-200',
    };
  };

  const config = getStatusConfig();
  const StatusIcon = config.icon;

  return (
    <div className={`rounded-lg border p-3 ${config.bg}`}>
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 w-6 h-6 rounded-full bg-slate-200 flex items-center justify-center text-xs font-semibold text-slate-600">
          {index + 1}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <StatusIcon className={`h-4 w-4 ${config.iconColor}`} />
              <p className="font-medium text-sm text-slate-800">{trace.ruleName}</p>
            </div>
            <Badge variant={config.badge} className="text-xs">
              {config.text}
            </Badge>
          </div>
          <p className="text-xs text-slate-500 mt-1 ml-6">{trace.reason}</p>
        </div>
      </div>
    </div>
  );
}
